import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';
import { uploadMultipart, createConcurrencyPool, estimatePartCount } from './uploadToS3';
import { convertImageToJpeg } from './convertImageToJpeg';

// Visibility is a visit-wide decision made once, at Submit (the familyOnly
// checkbox) — not chosen per item at add-time. AddVisitMedia requires SOME
// value on every placeholder, so every item gets this fixed provisional
// value; FinalizeVisit overwrites it on every item to match the real
// checkbox state when the visit is actually submitted.
const PROVISIONAL_VISIBILITY = 'public';

// Outer retry cap — 1E.3: scope narrowed from "reserve + upload" as one
// unit (the old single-PUT design) to just the two failure modes that
// actually need a fresh AddVisitMedia call: the initial reservation
// failing, or the final CompleteVisitMediaParts call failing after every
// part already succeeded. A part-upload failure does NOT go through this
// loop — uploadMultipart already retries each part 5 times internally
// before giving up, and re-running AddVisitMedia at that point would throw
// away an otherwise-healthy multipart session and any parts already on S3
// for nothing. See attemptWithRetry's resumeState handling below.
const MAX_ATTEMPTS = 3;

// 1E.3a decision: one pool, shared across every file added in this session
// — not one per file. Prevents N files each opening their own 2-3 chunk
// streams and collectively saturating a weak connection far more than any
// single file would on its own. Created once per hook instance via
// getPool() below, not per file. (Per-part stall timeout now lives in
// uploadToS3.js's PART_TIMEOUT_MS, since it's uniform across types once
// chunking is uniform.)
const GLOBAL_CHUNK_CONCURRENCY = 3;

// Recipe Principle 6: poll only while awaiting confirmation, short interval,
// self-terminating, capped so a genuinely stuck confirmation doesn't poll
// forever. The cap applies ONLY to the initial Uploading -> confirmed
// transition (waiting on the S3-event handler to run, which should be
// near-instant) — NOT to Processing, which has no cap at all: transcode
// time is inherently variable, and a stuck Processing item is not a
// failure (Submit already doesn't require it to finish).
const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 18000;
const PROCESSING_POLL_INTERVAL_MS = 5000;

// 1E.4 (ChatGPT feedback item 10): anti-abuse / sane-usage caps. Must
// match AddVisitMedia's MAX_FILE_SIZE_BYTES/MAX_ITEMS_PER_VISIT exactly —
// the backend remains the real enforcement layer regardless of what this
// client check does; this exists purely to give immediate, no-round-trip
// feedback instead of waiting on a rejected request.
export const MAX_FILE_SIZE_BYTES = 500 * 1024 * 1024; // 500MB
export const MAX_ITEMS_PER_VISIT = 12;
export const MAX_TOTAL_BYTES_PER_VISIT = MAX_ITEMS_PER_VISIT * MAX_FILE_SIZE_BYTES;

// Only items that actually hold (or held) a real reservation count toward
// the cap — a failed tile (wrong type, oversized, over-cap) never became
// a real placeholder and shouldn't count against the limit it was itself
// rejected by.
export function computeCountableTotals(items) {
    const countable = items.filter(item => item.status !== 'failed');
    const itemCount = countable.length;
    const totalBytes = countable.reduce((sum, item) => sum + (item.file?.size || 0), 0);
    return { itemCount, totalBytes };
}

// Accessibility: a single, natural-language announcement covering
// everything that changed between two renders — status transitions and
// removals — for a shared aria-live region. Not constrained by tile pixel
// space the way visual labels are, so this can be a full sentence rather
// than a short tag.
export function typeLabel(type) {
    if (type === 'photo') return 'Photo';
    if (type === 'video') return 'Video';
    if (type === 'audio') return 'Audio';
    return 'File';
}

export function describeStatusForAnnouncement(item, previousStatus) {
    const label = typeLabel(item.type);
    if (item.status === 'uploading') {
        // The only way an item reaches 'uploading' with a KNOWN previous
        // status is a manual Retry (a brand-new item's first-ever state is
        // never announced — see computeStatusAnnouncement).
        return previousStatus === 'failed' ? `Retrying ${label.toLowerCase()} upload.` : `${label} uploading.`;
    }
    if (item.status === 'processing') return `${label} processing.`;
    if (item.status === 'complete') return `${label} upload complete.`;
    if (item.status === 'failed') {
        if (item.staleFile) return `${label} upload failed — file expired. Please remove and try again.`;
        if (item.faultyFile) return `${label} upload failed — file could not be processed.`;
        if (item.validationFailed) {
            const reasonText = {
                file_too_large: 'file too large',
                too_many_items: 'too many items',
                visit_too_large: 'visit size limit exceeded',
            }[item.validationReason] || 'not allowed';
            return `${label} upload failed — ${reasonText}.`;
        }
        return `${label} upload failed.`;
    }
    return `${label} status updated.`;
}

export function computeStatusAnnouncement(previousItemsByClientId, currentItems) {
    const messages = [];
    const currentIds = new Set();

    currentItems.forEach(item => {
        currentIds.add(item.clientId);
        const prev = previousItemsByClientId[item.clientId];

        if (!prev) {
            // Newly added this render. A fresh item's own arrival is
            // already visually obvious immediately, and announcing every
            // file in a multi-select batch would be noisy — EXCEPT a
            // client-side validation failure (oversized file, over-cap,
            // unrecognized type), which never passes through 'uploading'
            // at all: 'failed' is its first and only state, so it must be
            // announced now or it's never announced at all.
            if (item.status === 'failed') {
                messages.push(describeStatusForAnnouncement(item, null));
            }
            return;
        }

        if (prev.status !== item.status) {
            messages.push(describeStatusForAnnouncement(item, prev.status));
        }
    });

    // Anything present before but missing now was removed (X, or Submit's
    // force-removal) — announce using its last-known type, since a
    // removed item is gone from currentItems entirely.
    Object.entries(previousItemsByClientId).forEach(([clientId, prev]) => {
        if (!currentIds.has(clientId)) {
            messages.push(`${typeLabel(prev.type)} removed.`);
        }
    });

    return messages.join(' ');
}

export function validateNewFile(fileSize, existingItemCount, existingTotalBytes) {
    if (fileSize > MAX_FILE_SIZE_BYTES) {
        return {
            error: `This file is too large (max ${Math.round(MAX_FILE_SIZE_BYTES / (1024 * 1024))}MB)`,
            reason: 'file_too_large',
        };
    }
    if (existingItemCount >= MAX_ITEMS_PER_VISIT) {
        return { error: `This visit already has the maximum of ${MAX_ITEMS_PER_VISIT} items`, reason: 'too_many_items' };
    }
    if (existingTotalBytes + fileSize > MAX_TOTAL_BYTES_PER_VISIT) {
        return { error: "Adding this file would exceed this visit's total size limit", reason: 'visit_too_large' };
    }
    return { ok: true };
}

function getTypeFromMime(mimeType) {
    if (!mimeType) return null;
    if (mimeType.startsWith('image/')) return 'photo';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    return null;
}

const CHECKPOINT_TABLES = {
    photo: { added: 16, compressed: 32, reserved: 48, uploaded: 64, assembled: 80, confirmed: 100 },
    simple: { added: 20, reserved: 40, uploaded: 60, assembled: 80, confirmed: 100 },
};

export function checkpointValue(tableName, stage) {
    return CHECKPOINT_TABLES[tableName]?.[stage] ?? null;
}

export function resolveInitialProgress(type, fileSize) {
    if (type === 'photo') {
        return {
            checkpointTable: 'photo',
            uploadProgress: { done: checkpointValue('photo', 'added'), total: 100 },
        };
    }
    const partCount = estimatePartCount(fileSize);
    if (partCount <= 1) {
        return {
            checkpointTable: 'simple',
            uploadProgress: { done: checkpointValue('simple', 'added'), total: 100 },
        };
    }
    return { checkpointTable: null, uploadProgress: { done: 0, total: partCount } };
}

export function resolvePostCompressionProgress(fileSize) {
    const partCount = estimatePartCount(fileSize);
    if (partCount <= 1) {
        return {
            checkpointTable: 'photo',
            uploadProgress: { done: checkpointValue('photo', 'compressed'), total: 100 },
        };
    }
    return { checkpointTable: null, uploadProgress: { done: 0, total: partCount } };
}

// Converts the {partNumber: eTag} map uploadMultipart works with into the
// [{partNumber, eTag}] array CompleteVisitMediaParts expects, in ascending
// order (S3 requires parts listed ascending, though the Lambda also
// sorts defensively — this just avoids relying on that).
export function toPartsArray(completedPartsMap) {
    return Object.entries(completedPartsMap)
        .map(([partNumber, eTag]) => ({ partNumber: Number(partNumber), eTag }))
        .sort((a, b) => a.partNumber - b.partNumber);
}

/**
 * Manages the full lifecycle of a visit's media items against the
 * draft-first, claim-first-placeholder flow described in the DoZESQ Media
 * Upload Recipe: compress (photos) -> AddVisitMedia (initiates a multipart
 * session) -> PUT every part through a shared concurrency pool ->
 * CompleteVisitMediaParts -> poll GetVisitMediaStatus until server-confirmed
 * -> Complete/Processing.
 *
 * 1E.3: every file always goes through multipart (a small file just
 * resolves to 1 part) — no single-PUT branch. Chunk uploads across every
 * file in the session share one global concurrency pool, not one per file.
 *
 * @param {string|null} visitId - null until CreateDraftVisit resolves; addFiles is a no-op until then.
 */
export function useVisitMediaUpload(visitId) {
    const [items, setItems] = useState([]);

    // Refs mirroring/backing React state, for synchronous access inside
    // async callbacks (avoids stale-closure races against setState).
    const itemsRef = useRef([]);
    const abortControllersRef = useRef(new Map());
    const pollTimersRef = useRef(new Map());
    // Lazily created once per hook instance, shared by every file's chunk
    // uploads for the life of this modal session — this is what makes the
    // concurrency cap global rather than per-file.
    const poolRef = useRef(null);
    const getPool = useCallback(() => {
        if (!poolRef.current) poolRef.current = createConcurrencyPool(GLOBAL_CHUNK_CONCURRENCY);
        return poolRef.current;
    }, []);
    // Tracks an X click that arrived while AddVisitMedia was still in
    // flight — before item.mediaId existed for removeItem to act on.
    // Without this, the reservation resolves after the tile is already
    // gone, orphaning a DynamoDB placeholder (and the S3 upload proceeds
    // anyway) with nothing left in the UI to clean it up.
    const cancelledRef = useRef(new Set());
    // Tracks mediaIds the person has X'd but whose server-side delete
    // hasn't been confirmed yet — either still retrying in the background,
    // or retries exhausted. Read at Submit time so FinalizeVisit can force
    // -remove anything still outstanding, instead of Submit blocking on an
    // item that's already gone from the UI with no way to see or fix it
    // (reproduced 2026-08-01: a genuine network drop left an orphaned
    // 'uploading' placeholder Submit couldn't get past).
    const pendingRemovalsRef = useRef(new Set());
    const removalRetryTimersRef = useRef(new Map());

    const updateItem = useCallback((clientId, patch) => {
        setItems(prev => {
            const next = prev.map(item =>
                item.clientId === clientId ? { ...item, ...patch } : item
            );
            itemsRef.current = next;
            return next;
        });
    }, []);

    // ── Confirmation poll (Recipe Principle 6) ──────────────────────────

    const startConfirmationPoll = useCallback((clientId, mediaId) => {
        const startTime = Date.now();
        // Tracks whether we've ever confirmed this item is legitimately
        // past Uploading (i.e. Processing) — once true, the timeout no
        // longer applies, including to a network hiccup on a later poll.
        let confirmedBeyondUploading = false;

        // 1E.4 (ChatGPT feedback item 12): once real S3 reconciliation has
        // confirmed the object genuinely exists, this item's poll loop
        // behaves like the 'processing' branch below (uncapped, slower
        // interval) even though its DynamoDB uploadStatus is still
        // 'uploading' — the object is real, we're only waiting on a
        // delayed event notification catching up, not a genuine failure.
        // Kept as a local flag, not server state: no tile/status change is
        // wanted here (Jeff's call — reuse the existing Uploading look
        // rather than add a new visual state), so this only needs to
        // affect this poll loop's own timeout behavior.
        let confirmedViaReconciliation = false;

        const poll = async () => {
            // Item may have been removed (X clicked) while a poll was
            // already scheduled — bail out silently rather than resurrect it.
            if (!itemsRef.current.some(item => item.clientId === clientId)) return;

            const giveUp = () => {
                pollTimersRef.current.delete(clientId);
                updateItem(clientId, {
                    status: 'failed',
                    errorMessage: "We couldn't confirm this upload finished. Please try again.",
                });
            };

            const scheduleNext = (intervalMs) => {
                const timeoutId = setTimeout(poll, intervalMs);
                pollTimersRef.current.set(clientId, timeoutId);
            };

            // Only asks the backend to reconcile against real S3 state at
            // the one moment it matters — when this poll is about to hit
            // its timeout and would otherwise give up. Every earlier poll
            // in this same loop omits it, so ordinary polling carries no
            // extra cost.
            const aboutToTimeOut = !confirmedBeyondUploading
                && !confirmedViaReconciliation
                && Date.now() - startTime >= POLL_TIMEOUT_MS;

            // 1E.4 (ChatGPT feedback round 2): once reconciliation has
            // confirmed the object exists but the item hasn't yet caught
            // up (the DB write from the repair attempt, or the original
            // event, hasn't landed YET), keep requesting reconcile=true
            // on every subsequent poll too — not just the one-shot at
            // timeout — so the backend's repair re-invoke keeps getting a
            // chance to run until status actually advances.
            const shouldRequestReconcile = aboutToTimeOut || confirmedViaReconciliation;

            try {
                const { statuses } = await api.getVisitMediaStatus(visitId, [mediaId], shouldRequestReconcile);
                const entry = statuses[mediaId];

                if (!entry) {
                    // Gone from the visit entirely (raced with a delete) —
                    // nothing left to confirm.
                    pollTimersRef.current.delete(clientId);
                    return;
                }

                const { uploadStatus: status, thumbnailUrl, objectExists } = entry;

                if (status === 'complete') {
                    pollTimersRef.current.delete(clientId);
                    // thumbnailUrl is only ever populated on the same
                    // completion event that sets status to 'complete'
                    // (both derived objects come from one MediaConvert
                    // job) — null for photos/audio, and for a video whose
                    // Frame Capture output couldn't be parsed (1E.2).
                    updateItem(clientId, { status: 'complete', thumbnailUrl });
                    return;
                }

                if (status === 'processing') {
                    // Legitimately confirmed and transcoding — no timeout
                    // from here on. Keep polling, slower, until it reaches
                    // 'complete' so the tile can eventually show a real
                    // thumbnail/playback instead of sitting on Processing
                    // for the rest of the session.
                    confirmedBeyondUploading = true;
                    updateItem(clientId, { status: 'processing' });
                    scheduleNext(PROCESSING_POLL_INTERVAL_MS);
                    return;
                }

                if (status === 'failed') {
                    // Bug found via real testing (2026-08-05): a genuine
                    // server-side terminal failure (e.g. MediaConvert
                    // ERROR/CANCELED, via markFailed) was never recognized
                    // here — this status only exists once an item has
                    // already passed through 'processing', at which point
                    // confirmedBeyondUploading is already true and the 18s
                    // timeout no longer applies, so the poll just silently
                    // continued checking forever with nothing to ever stop
                    // it.
                    //
                    // retryDisabled: true — a server-side terminal failure
                    // at this stage means the file itself is the problem
                    // (e.g. a structurally corrupt video MediaConvert
                    // can't open), not the network or session. The upload
                    // already fully succeeded once; re-uploading the exact
                    // same bytes again cannot produce a different outcome.
                    // Same reasoning as the expired-presigned-URL case —
                    // an offered Retry that's guaranteed to fail identically
                    // is worse than no Retry at all.
                    pollTimersRef.current.delete(clientId);
                    updateItem(clientId, {
                        status: 'failed',
                        retryDisabled: true,
                        faultyFile: true,
                        errorMessage: "This file couldn't be processed — it may be corrupted or in an unsupported format. Please remove it and try a different file.",
                    });
                    return;
                }

                // status === 'uploading' — still waiting on the initial
                // S3-event confirmation. The 18s cap applies here UNLESS
                // reconciliation has confirmed (this poll, or an earlier
                // one) that the object genuinely already exists on S3 —
                // in that case this is a delayed event notification, not
                // a failure, and polling continues uncapped, same
                // treatment as 'processing' above. Status/tile stay
                // exactly as they are; only the timeout behavior changes.
                if (objectExists === true) {
                    confirmedViaReconciliation = true;
                }

                if (aboutToTimeOut && !confirmedViaReconciliation) {
                    // Reconciliation ran this poll (aboutToTimeOut was
                    // true) and either confirmed the object is genuinely
                    // absent, or the check itself couldn't run/complete —
                    // either way, no positive confirmation exists, so this
                    // is treated as the real failure it most likely is.
                    giveUp();
                } else if (confirmedViaReconciliation) {
                    scheduleNext(PROCESSING_POLL_INTERVAL_MS);
                } else {
                    scheduleNext(POLL_INTERVAL_MS);
                }
            } catch (error) {
                // Network hiccup on the poll call itself. Once we're
                // confirmed past Uploading, a blip here just means retry
                // quietly — no cap. Otherwise, the same Uploading-phase
                // timeout budget applies as above.
                if (confirmedBeyondUploading) {
                    scheduleNext(PROCESSING_POLL_INTERVAL_MS);
                } else if (Date.now() - startTime >= POLL_TIMEOUT_MS) {
                    giveUp();
                } else {
                    scheduleNext(POLL_INTERVAL_MS);
                }
            }
        };

        poll();
    }, [visitId, updateItem]);

    // ── Background delete retry (for X'd items) ─────────────────────────

    const REMOVAL_RETRY_DELAYS_MS = [2000, 5000, 15000];

    const attemptRemovalDelete = useCallback((mediaId, attempt = 0) => {
        api.deleteVisitMedia({ visitId, mediaId })
            .then(() => {
                pendingRemovalsRef.current.delete(mediaId);
                removalRetryTimersRef.current.delete(mediaId);
            })
            .catch(error => {
                console.error(`Error deleting visit media ${mediaId} (attempt ${attempt + 1}):`, error);
                if (attempt < REMOVAL_RETRY_DELAYS_MS.length) {
                    const timerId = setTimeout(
                        () => attemptRemovalDelete(mediaId, attempt + 1),
                        REMOVAL_RETRY_DELAYS_MS[attempt]
                    );
                    removalRetryTimersRef.current.set(mediaId, timerId);
                } else {
                    // Retries exhausted, silently — no UI error (per Jeff's
                    // call, removal should never surface a "try again" to
                    // the person). pendingRemovalsRef still holds it, so
                    // FinalizeVisit's removedMediaIds cleanup is the
                    // fallback if Submit happens first; if the draft is
                    // abandoned instead, DeleteDraftVisit or the S3
                    // lifecycle rule eventually catches it regardless.
                    removalRetryTimersRef.current.delete(mediaId);
                }
            });
    }, [visitId]);

    // ── Upload a session's parts, then complete it ──────────────────────
    //
    // Split out from attemptWithRetry so a manual Retry can call straight
    // into this with the existing mediaId/uploadId/key/parts and whatever
    // partial progress already exists, WITHOUT going through a fresh
    // AddVisitMedia reservation — that's the actual partial-resume
    // mechanism (1E.3a decision).

    const runUploadSession = useCallback(async (clientId, file, session, alreadyCompletedParts) => {
        const { mediaId, uploadId, key, parts } = session;
        const item = itemsRef.current.find(i => i.clientId === clientId);
        const checkpointTable = item?.checkpointTable ?? null;

        updateItem(clientId, {
            mediaId, uploadId, key, parts,
            status: 'uploading',
            errorMessage: undefined,
            uploadProgress: checkpointTable
                ? { done: checkpointValue(checkpointTable, 'reserved'), total: 100 }
                : { done: 0, total: 100 }, // corrected immediately below by uploadMultipart's own resumed-baseline report
        });

        const controller = new AbortController();
        abortControllersRef.current.set(clientId, controller);

        let completedParts;
        try {
            completedParts = await uploadMultipart(file, { parts }, {
                pool: getPool(),
                controller,
                alreadyCompletedParts,
                onPartComplete: checkpointTable
                    ? () => updateItem(clientId, { uploadProgress: { done: checkpointValue(checkpointTable, 'uploaded'), total: 100 } })
                    : undefined,
                onProgress: checkpointTable
                    ? undefined
                    : (pct) => updateItem(clientId, { uploadProgress: { done: pct, total: 100 } }),
            });
        } catch (error) {
            abortControllersRef.current.delete(clientId);

            if (error.name === 'AbortError') {
                return;
            }

            // 1E.4: PresignedUrlExpiredError no longer gets a dead-end
            // special case here — it now falls through to the same
            // resumeState path as every other part failure. A dead link
            // doesn't mean the underlying multipart session is dead; the
            // manual Retry below now checks that directly against S3/
            // DynamoDB (via ResumeVisitMedia) instead of assuming the
            // worst from a single 403.
            updateItem(clientId, {
                status: 'failed',
                errorMessage: error.message || 'Upload failed. Please try again.',
                resumeState: { mediaId, uploadId, key, parts, completedParts: error.completedParts || alreadyCompletedParts },
            });
            return;
        }

        abortControllersRef.current.delete(clientId);

        try {
            await api.completeVisitMediaUpload({
                visitId,
                mediaId,
                key,
                uploadId,
                parts: toPartsArray(completedParts),
            });
        } catch (error) {
            updateItem(clientId, {
                status: 'failed',
                errorMessage: error.message || "We couldn't finish this upload. Please try again.",
                resumeState: { mediaId, uploadId, key, parts, completedParts },
            });
            return;
        }

        if (checkpointTable) {
            updateItem(clientId, { uploadProgress: { done: checkpointValue(checkpointTable, 'assembled'), total: 100 } });
        }

        startConfirmationPoll(clientId, mediaId);
    }, [visitId, updateItem, getPool, startConfirmationPoll]);

    // ── Upload attempt — Initiate (AddVisitMedia), with outer retry ────
    //
    // 1E.3: this loop's scope narrowed to JUST the initiate call. A file
    // that already has a live session (resumeState from a prior failed
    // attempt) skips straight to runUploadSession and never re-enters
    // this loop at all.

    const attemptWithRetry = useCallback(async (clientId, type, file) => {
        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
            try {
                const session = await api.addVisitMedia({
                    visitId,
                    type,
                    visibility: PROVISIONAL_VISIBILITY,
                    contentType: file.type,
                    fileSize: file.size,
                });

                if (cancelledRef.current.has(clientId)) {
                    cancelledRef.current.delete(clientId);
                    api.deleteVisitMedia({ visitId, mediaId: session.mediaId }).catch(() => {});
                    return;
                }

                await runUploadSession(clientId, file, session, {});
                return;
            } catch (error) {
                // 1E.4: broadened from 409-only to any 4xx — a validation
                // failure (wrong content type, size/count/total cap
                // rejection, expired draft) is deterministic given the
                // same request; retrying it can only ever fail identically
                // again. Only a 5xx or a network failure genuinely might
                // succeed on retry.
                const unrecoverable = error.status >= 400 && error.status < 500;

                if (attempt === MAX_ATTEMPTS || unrecoverable) {
                    if (error.reason) {
                        // A cap/size rejection the client-side pre-check in
                        // addFiles didn't catch (e.g. a second tab, or a
                        // race against another file in the same batch) —
                        // same visible treatment as the pre-check gives,
                        // just arriving a beat later from the backend.
                        updateItem(clientId, {
                            status: 'failed',
                            validationFailed: true,
                            validationReason: error.reason,
                            retryDisabled: true,
                            errorMessage: error.message || 'Upload failed. Please try again.',
                        });
                    } else {
                        updateItem(clientId, {
                            status: 'failed',
                            errorMessage: error.message || 'Upload failed. Please try again.',
                        });
                    }
                    return;
                }
            }
        }
    }, [visitId, updateItem, runUploadSession]);

    // ── Add files ────────────────────────────────────────────────────────

    const processAndUpload = useCallback(async (clientId, type, file) => {
        let workingFile = file;

        if (type === 'photo') {
            // Always resize/compress, even for files that already arrive as
            // JPEG — this used to be skipped for already-JPEG files, which
            // let an unprocessed multi-megabyte phone-camera photo through
            // completely untouched. convertImageToJpeg now caps dimensions
            // and steps down through quality levels to keep every photo
            // comfortably under the 5MB multipart chunk size, so photos
            // stay single-part regardless of connection quality.
            try {
                workingFile = await convertImageToJpeg(file);
            } catch (error) {
                updateItem(clientId, {
                    status: 'failed',
                    errorMessage: error.message || "We couldn't process this photo.",
                });
                return;
            }
            updateItem(clientId, resolvePostCompressionProgress(workingFile.size));
        }

        const previewUrl = URL.createObjectURL(workingFile);
        updateItem(clientId, { file: workingFile, previewUrl });

        await attemptWithRetry(clientId, type, workingFile);
    }, [updateItem, attemptWithRetry]);

    const addFiles = useCallback((fileList) => {
        if (!visitId) return; // draft isn't ready yet — caller should guard against this too

        const files = Array.from(fileList);

        // 1E.4 bugfix: seeded once from itemsRef.current (safe here — it
        // reflects whatever already-flushed state exists at the START of
        // this call), then tracked locally and incremented synchronously
        // as each file in THIS batch passes its check. Necessary because
        // React batches every setItems call below across this whole
        // forEach — itemsRef.current does NOT reflect earlier files from
        // the SAME batch until after this function returns and React
        // actually processes the batch. Without this local tracking, a
        // 13-file multi-select checked every file against the same stale
        // pre-batch count and let all 13 through (found in testing,
        // 2026-08-04).
        let runningTotals = computeCountableTotals(itemsRef.current);

        files.forEach(file => {
            const type = getTypeFromMime(file.type);
            const clientId = crypto.randomUUID();

            if (!type) {
                // Silently dropping this with no feedback is exactly the
                // kind of thing the Recipe was written to prevent — better
                // a visible Failed tile than a file that just vanishes.
                setItems(prev => {
                    const next = [...prev, {
                        clientId,
                        type: 'photo', // arbitrary — MediaTile needs *a* type to render an icon; content never uploads
                        status: 'failed',
                        mediaId: null,
                        file: null,
                        previewUrl: undefined,
                        errorMessage: `We couldn't recognize "${file.name}" as a photo, video, or audio file.`,
                        retryDisabled: true,
                    }];
                    itemsRef.current = next;
                    return next;
                });
                return;
            }

            const capacityCheck = validateNewFile(file.size, runningTotals.itemCount, runningTotals.totalBytes);

            if (capacityCheck.error) {
                setItems(prev => {
                    const next = [...prev, {
                        clientId,
                        type,
                        status: 'failed',
                        mediaId: null,
                        file: null,
                        previewUrl: undefined,
                        errorMessage: capacityCheck.error,
                        validationFailed: true,
                        validationReason: capacityCheck.reason,
                        retryDisabled: true,
                    }];
                    itemsRef.current = next;
                    return next;
                });
                return;
            }

            // This file was just accepted — counts toward the running
            // total for the REST of this batch.
            runningTotals = {
                itemCount: runningTotals.itemCount + 1,
                totalBytes: runningTotals.totalBytes + file.size,
            };

            const { checkpointTable, uploadProgress } = resolveInitialProgress(type, file.size);

            const newItem = {
                clientId,
                type,
                status: 'uploading',
                mediaId: null,
                file,
                previewUrl: undefined,
                errorMessage: undefined,
                uploadProgress,
                checkpointTable,
            };

            setItems(prev => {
                const next = [...prev, newItem];
                itemsRef.current = next;
                return next;
            });

            processAndUpload(clientId, type, file);
        });
    }, [visitId, processAndUpload]);

    // ── Remove (X) ───────────────────────────────────────────────────────

    const removeItem = useCallback((clientId) => {
        const item = itemsRef.current.find(i => i.clientId === clientId);

        const controller = abortControllersRef.current.get(clientId);
        if (controller) {
            controller.abort();
            abortControllersRef.current.delete(clientId);
        }

        const pollTimer = pollTimersRef.current.get(clientId);
        if (pollTimer) {
            clearTimeout(pollTimer);
            pollTimersRef.current.delete(clientId);
        }

        if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);

        // Tile removal is always immediate and optimistic — per Jeff's
        // call, X must never show a failure state. Server-side cleanup is
        // best-effort with background retry (below); if it's still
        // unresolved by Submit, FinalizeVisit's removedMediaIds cleanup is
        // the authoritative fallback, not this call.
        setItems(prev => {
            const next = prev.filter(i => i.clientId !== clientId);
            itemsRef.current = next;
            return next;
        });

        if (item?.mediaId) {
            pendingRemovalsRef.current.add(item.mediaId);
            attemptRemovalDelete(item.mediaId);
        } else if (item) {
            // AddVisitMedia hasn't resolved yet — no mediaId to delete
            // against yet. Flag it so attemptWithRetry cleans up the
            // reservation itself the moment it resolves, instead of the
            // upload silently proceeding with no tile left to cancel it.
            cancelledRef.current.add(clientId);
        }
    }, [attemptRemovalDelete]);

    // ── Resume check (manual Retry when a live session may already exist) ──
    //
    // 1E.4: rather than trusting the client's own memory that resumeState's
    // uploadId/parts are still good (the gap ChatGPT flagged — a presigned
    // URL or the whole multipart session may have gone stale while the
    // person was away), Retry now asks ResumeVisitMedia first. Per Jeff's
    // UX call, the tile shows the normal Uploading UI immediately on tap —
    // the resume check itself IS the "checking" state, no separate
    // spinner — then either continues seamlessly (resumable) or drops to a
    // distinct Stale File failure (not).

    const resumeAndUploadSession = useCallback(async (clientId, file, resumeState) => {
        const { mediaId, uploadId, key, parts } = resumeState;

        updateItem(clientId, { status: 'uploading', errorMessage: undefined });

        let result;
        try {
            result = await api.resumeVisitMedia({
                visitId,
                mediaId,
                uploadId,
                key,
                totalParts: parts.length,
            });
        } catch (error) {
            // The check call itself failed (network/server error, not a
            // stale-session verdict) — the existing resumeState is still
            // worth trying again, so this is a normal retryable failure.
            updateItem(clientId, {
                status: 'failed',
                errorMessage: error.message || "We couldn't check this upload. Please try again.",
                resumeState,
            });
            return;
        }

        if (!result.resumable) {
            updateItem(clientId, {
                status: 'failed',
                staleFile: true,
                retryDisabled: true,
                errorMessage: undefined,
                resumeState: undefined,
            });
            return;
        }

        runUploadSession(
            clientId,
            file,
            { mediaId, uploadId, key, parts: result.parts },
            result.completedParts
        );
    }, [visitId, updateItem, runUploadSession]);

    // ── Retry (manual, from a Failed tile) ──────────────────────────────

    const retryItem = useCallback((clientId) => {
        const item = itemsRef.current.find(i => i.clientId === clientId);
        if (!item || !item.file || item.status === 'uploading') return; // no file to retry, or already retrying

        if (item.resumeState) {
            resumeAndUploadSession(clientId, item.file, item.resumeState);
            return;
        }

        updateItem(clientId, { status: 'uploading', errorMessage: undefined });
        attemptWithRetry(clientId, item.type, item.file);
    }, [updateItem, attemptWithRetry, resumeAndUploadSession]);

    // ── Reset (explicit) ─────────────────────────────────────────────────
    // The modal that owns this hook doesn't unmount between opens — it
    // stays mounted with `isOpen` just toggling a conditional render — so
    // the unmount-cleanup effect below never fires between visits. Callers
    // must call reset() explicitly whenever the modal closes, or state from
    // an abandoned visit would bleed into the next one opened in the same
    // session.
    //
    // Purely client-side teardown (abort in-flight requests, clear timers,
    // revoke preview URLs). Does NOT call deleteVisitMedia per item —
    // DeleteDraftVisit already proactively cleans up every media item's S3
    // object server-side in one shot when the draft itself is deleted, so
    // duplicating that here per-item would just be wasted calls.
    const reset = useCallback(() => {
        abortControllersRef.current.forEach(controller => controller.abort());
        abortControllersRef.current.clear();
        pollTimersRef.current.forEach(timerId => clearTimeout(timerId));
        pollTimersRef.current.clear();
        cancelledRef.current.clear();
        removalRetryTimersRef.current.forEach(timerId => clearTimeout(timerId));
        removalRetryTimersRef.current.clear();
        pendingRemovalsRef.current.clear();
        itemsRef.current.forEach(item => {
            if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
        });
        itemsRef.current = [];
        setItems([]);
    }, []);

    // ── Cleanup on unmount ───────────────────────────────────────────────
    // Belt-and-suspenders for the case where this hook IS used somewhere
    // that unmounts normally (or the app itself closes/navigates away).

    useEffect(() => {
        return () => {
            abortControllersRef.current.forEach(controller => controller.abort());
            pollTimersRef.current.forEach(timerId => clearTimeout(timerId));
            removalRetryTimersRef.current.forEach(timerId => clearTimeout(timerId));
            itemsRef.current.forEach(item => {
                if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
            });
        };
    }, []);

    // ── Derived state for Submit-gating ─────────────────────────────────

    const hasBlockingItems = items.some(
        item => item.status === 'uploading' || item.status === 'failed'
    );

    return {
        items,
        addFiles,
        removeItem,
        retryItem,
        reset,
        hasBlockingItems,
        // Snapshot, not live state — read once at Submit time, doesn't
        // need to trigger re-renders as it changes in the background.
        getPendingRemovalMediaIds: () => Array.from(pendingRemovalsRef.current),
    };
}